import {
  Component,
  Input,
  EventEmitter,
  Output,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { HttpService } from '../services/http-service';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { BeadCanvas, EditMode } from '../peditor/beadcanvas';
import { ProfileModal } from '../modals/profile-modal/profile-modal';
import { InsertRowsColsModal, InsertRowsColsModalResult } from '../modals/insert-rows-cols-modal/insert-rows-cols-modal';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LocalStorageKeys } from '../helpers/constants';
import { PosthogService } from '../services/posthog.service';
import { OnboardingService } from '../services/onboarding.service';
import { SubscriptionTypeEnum } from '../models/user/user-model';
import { GoogleAuthService } from '../services/google-auth.service';

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.css'],
})
export class ToolbarComponent {
  @Input()
  windowWidth: number;
  @Input()
  windowHeight: number;

  @Input()
  bcanvas: BeadCanvas;

  @Input()
  rightSidebarOpen: boolean = true;

  @Output()
  toolbarActionFired: EventEmitter<ToolbarActionFired> =
    new EventEmitter<ToolbarActionFired>();

  @ViewChild('fileUpload', { static: true })
  private fileUpload: ElementRef;

  @ViewChild('dotsMenu', { static: true })
  private dotsMenu: ElementRef;

  @ViewChild('fileMenuTrigger')
  private fileMenuTrigger: MatMenuTrigger;

  @ViewChild('imageMenuTrigger')
  private imageMenuTrigger: MatMenuTrigger;

  @ViewChild('mobileFileMenuTrigger')
  private mobileFileMenuTrigger: MatMenuTrigger;

  @ViewChild('mobileImageMenuTrigger')
  private mobileImageMenuTrigger: MatMenuTrigger;

  @ViewChildren(MatMenuTrigger)
  private menuTriggers: QueryList<MatMenuTrigger>;

  editModes = EditMode;

  iconNameImageVisibility: string = 'visibility';
  iconNameRealisticBeadsVisibility: string = 'label_off';

  currentRotatedPatternTooltip: string = '';
  isRotatedPattern: boolean = false;

  minMainMenuWidth = 430;

  minFullToolbarWidth = 900;
  minLargeToolbarWidth = 750;
  minMediumToolbarWidth = 510;
  minSmallToolbarWidth = 330;

  undoShortcut = '';
  redoShortcut = '';
  zoomInShortcut = '';
  zoomOutShortcut = '';
  resetZoomShortcut = '';
  brushShortcut = '';
  eraserShortcut = '';
  panShortcut = '';

  // Usage tracking for free users
  isFreeUser: boolean = false;
  userPatternCount: number = 0;
  remainingFreePatterns: number = 3;
  usagePercentage: number = 0;
  profileMenuOpen: boolean = false;

  // Menu hover behavior
  private isMenuOpen: boolean = false;
  private currentOpenTrigger: MatMenuTrigger | null = null;
  private menuCloseTimeout: any = null;

  constructor(
    private router: Router,
    public httpService: HttpService,
    private googleAuthService: GoogleAuthService,
    public dialog: MatDialog,
    private deviceService: DeviceDetectorService,
    private posthog: PosthogService,
    public onboardingService: OnboardingService,
    private cdr: ChangeDetectorRef
  ) {
    if (this.deviceService.isDesktop()) {
      // Detect platform for platform-specific shortcuts
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

      if (isMac) {
        // Mac shortcuts with Command key symbol
        this.undoShortcut = '— ⌘Z';
        this.redoShortcut = '— ⌘⇧Z';
        this.zoomInShortcut = '— ⌘+';
        this.zoomOutShortcut = '— ⌘-';
        this.resetZoomShortcut = '— ⌘0';
      } else {
        // Windows/Linux shortcuts with Ctrl
        this.undoShortcut = '— Ctrl+Z';
        this.redoShortcut = '— Ctrl+Y';
        this.zoomInShortcut = '— Ctrl++';
        this.zoomOutShortcut = '— Ctrl+-';
        this.resetZoomShortcut = '— Ctrl+0';
      }

      // Tool shortcuts are the same across platforms
      this.brushShortcut = '— B';
      this.eraserShortcut = '— E';
      this.panShortcut = '— Space';
    }
  }

  async ngOnInit(): Promise<void> {
    const nativeButton = (this.dotsMenu as any)._elementRef
      .nativeElement as HTMLElement;
    if (this.windowWidth <= this.minMainMenuWidth) {
      nativeButton.setAttribute(
        'data-hint',
        '<b>Image => Insert Image</b> to upload your own image.<br/><br/><hr/><br/><b>Image => Transfer Image</b> to convert uploaded image into a pattern<br/><br/>'
      );
      nativeButton.setAttribute('data-hint-position', 'bottom-middle');
    }

    // Initialize usage tracking
    await this.updateUsageTracking();

    // Register menu opener callback for onboarding service
    this.onboardingService.setMenuOpenerCallback((menu) => {
      this.openMenuForOnboarding(menu);
    });
  }

  public async updateUsageTracking(): Promise<void> {
    if (!this.httpService.user) {
      console.log('[Toolbar] User not loaded yet');
      return;
    }

    this.isFreeUser = this.httpService.user.subscriptionType === SubscriptionTypeEnum.None;
    console.log('[Toolbar] Usage tracking - isFreeUser:', this.isFreeUser, 'subscriptionType:', this.httpService.user.subscriptionType);

    if (this.isFreeUser) {
      try {
        const patterns = await this.httpService.getPatterns(0, '');
        this.userPatternCount = patterns.length;
        this.remainingFreePatterns = Math.max(0, 3 - this.userPatternCount);
        this.usagePercentage = Math.min(100, (this.userPatternCount / 3) * 100);
        console.log('[Toolbar] Usage updated - patterns:', this.userPatternCount, 'remaining:', this.remainingFreePatterns, 'percentage:', this.usagePercentage);
      } catch (error) {
        console.error('Error updating usage tracking:', error);
        this.userPatternCount = 0;
        this.remainingFreePatterns = 3;
        this.usagePercentage = 0;
      }
    }
  }

  // Getter to check if user is free in real-time
  get isUserFree(): boolean {
    return this.httpService?.user?.subscriptionType === SubscriptionTypeEnum.None;
  }

  // Update usage tracking when profile menu opens
  public onProfileMenuOpened(): void {
    console.log('[Toolbar] Profile menu opened, updating usage tracking');
    this.profileMenuOpen = true;
    this.updateUsageTracking();
  }

  public onProfileMenuClosed(): void {
    this.profileMenuOpen = false;
  }

  /**
   * Handle menu opened - set flag that a menu is now open
   */
  public onMenuOpened(): void {
    // Clear any pending close timeout
    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
      this.menuCloseTimeout = null;
    }

    this.isMenuOpen = true;
  }

  /**
   * Handle mouse enter on menu buttons - open menu if another menu is already open
   */
  public onMenuMouseEnter(trigger: MatMenuTrigger): void {
    if (this.isMenuOpen && trigger && !trigger.menuOpen) {
      // Clear any pending close timeout immediately
      if (this.menuCloseTimeout) {
        clearTimeout(this.menuCloseTimeout);
        this.menuCloseTimeout = null;
      }

      // Close the currently open menu
      if (this.currentOpenTrigger && this.currentOpenTrigger.menuOpen) {
        this.currentOpenTrigger.closeMenu();
      }

      // Open the new menu immediately
      trigger.openMenu();
      this.currentOpenTrigger = trigger;
    } else if (!this.isMenuOpen && trigger) {
      // Track the trigger when menu is opened by click
      this.currentOpenTrigger = trigger;
    }
  }

  /**
   * Handle menu closed - reset the flag when user closes or clicks away
   */
  public onMenuClosed(): void {
    // Clear any existing timeout
    if (this.menuCloseTimeout) {
      clearTimeout(this.menuCloseTimeout);
    }

    // Use a small delay to allow menu switching to complete
    // If a new menu opens within this time, it will clear this timeout
    this.menuCloseTimeout = setTimeout(() => {
      this.isMenuOpen = false;
      this.currentOpenTrigger = null;
      this.menuCloseTimeout = null;
    }, 150);
  }

  /**
   * Handle document clicks to close menus when clicking outside
   * Note: This only handles main menu bar hover functionality.
   * The mobile 3 dots menu uses hasBackdrop="true" and handles its own close behavior.
   */
  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: any): void {
    // Skip handling for mobile devices - let the backdrop handle closing
    // This prevents the 3 dots menu from closing immediately on mobile
    if (this.windowWidth <= this.minMainMenuWidth) {
      return;
    }

    // Skip if profile menu is open - it handles its own close behavior via backdrop
    if (this.profileMenuOpen) {
      return;
    }

    // Small delay to allow Angular to process the event first
    setTimeout(() => {
      // Check if any menu is open (excluding profile menu)
      const hasOpenMenu = this.menuTriggers?.some(trigger => trigger.menuOpen);

      if (!hasOpenMenu) {
        return;
      }

      // Get the target element
      const target = event.target as HTMLElement;

      if (!target) {
        return;
      }

      // Check if the click was on a menu button or inside a menu
      const clickedOnMenuButton = target.closest('button[mat-button]') ||
                                   target.closest('button[mat-icon-button]') ||
                                   target.closest('button[mat-menu-item]');
      const clickedInsideMenu = target.closest('.mat-menu-panel') ||
                                target.closest('.mat-menu-content') ||
                                target.closest('.cdk-overlay-pane');
      const clickedOnBackdrop = target.classList.contains('cdk-overlay-backdrop');

      // If clicked outside both menu buttons and menu panels, or on backdrop, close all menus
      if ((!clickedOnMenuButton && !clickedInsideMenu) || clickedOnBackdrop) {
        // Close all open menus
        this.menuTriggers?.forEach(trigger => {
          if (trigger.menuOpen) {
            trigger.closeMenu();
          }
        });
      }
    }, 50);
  }

  public refreshRotatedTooltip() {
    this.isRotatedPattern = this.bcanvas.isRotatedPattern();
    this.currentRotatedPatternTooltip = this.getCurrentRotatedPatternTooltip();
  }

  async newPattern() {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.New,
    });
  }

  async openPattern() {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.Open,
    });
  }

  async savePattern() {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.Save,
    });
  }

  canSavePattern(): boolean {
    return this.httpService && this.httpService.canSavePattern();
  }

  async saveAsPattern() {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.SaveAs,
    });
  }

  async printPattern() {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.Print,
    });
  }

  async uploadImage(files) {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.UploadImage,
      parameters: files,
    });

    this.fileUpload.nativeElement.value = '';
  }

  /**
   * Public method to programmatically trigger file upload dialog
   * Used by checklist component to guide users to insert image
   */
  public triggerFileUpload(): void {
    if (this.fileUpload?.nativeElement) {
      this.fileUpload.nativeElement.click();
    }
  }

  setEditMode(editMode: EditMode) {
    this.bcanvas.setEditMode(editMode);
  }

  getCurrentEditModeIcon(): string {
    switch (this.bcanvas.getEditMode()) {
      case EditMode.AddBead:
        return 'edit';
      case EditMode.Fill:
        return 'format_paint';
      case EditMode.RemoveBead:
        return 'clear';
      case EditMode.RemoveFill:
        return 'format_clear';
      case EditMode.PickBead:
        return 'colorize';
      case EditMode.Pan:
        return 'pan_tool';
    }

    return '';
  }

  getCurrentEditModeTooltip(): string {
    switch (this.bcanvas.getEditMode()) {
      case EditMode.AddBead:
        return this.httpService.getTransation().AddBead;
      case EditMode.Fill:
        return this.httpService.getTransation().Fill;
      case EditMode.RemoveBead:
        return this.httpService.getTransation().RemoveBead;
      case EditMode.RemoveFill:
        return this.httpService.getTransation().RemoveFill;
      case EditMode.PickBead:
        return this.httpService.getTransation().PickBead;
      case EditMode.Pan:
        return this.httpService.getTransation().Pan;
    }

    return '';
  }

  getCurrentRotatedPatternTooltip(): string {
    if (this.bcanvas.isRotatedPattern()) {
      return this.httpService.translation.RotatedPatternOn;
    } else {
      return this.httpService.translation.RotatedPatternOff;
    }
  }

  toggleRotatedPattern() {
    this.bcanvas.rotatedPattern(!this.bcanvas.isRotatedPattern());
    this.refreshRotatedTooltip();
  }

  setRotatedPattern(rotated: boolean) {
    this.bcanvas.rotatedPattern(rotated);
  }

  canToggleImageVisibility() {
    return this.bcanvas.getPatternImage();
  }

  isImageVisible() {
    return this.iconNameImageVisibility == 'visibility';
  }

  rotateImageLeft() {
    if (this.bcanvas) {
      this.bcanvas.rotateImageLeft();
    }
  }

  rotateImageRight() {
    if (this.bcanvas) {
      this.bcanvas.rotateImageRight();
    }
  }

  refreshIconNameImageVisibility(): string {
    if (this.bcanvas.getModel().getPatternImage()) {
      if (this.bcanvas.getModel().getPatternImage().visible) {
        return (this.iconNameImageVisibility = 'visibility');
      } else {
        return (this.iconNameImageVisibility = 'visibility_off');
      }
    } else {
      return (this.iconNameImageVisibility = 'visibility');
    }
  }

  toggleImageVisibility() {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.ToggleImageVisibility,
    });
    this.refreshIconNameImageVisibility();
  }

  refreshIconNameRealisticBeadsVisibility(): string {
    if (this.bcanvas.isRealisticBeadsEnabled()) {
      return (this.iconNameRealisticBeadsVisibility = 'label');
    } else {
      return (this.iconNameRealisticBeadsVisibility = 'label_off');
    }
  }

  toggleRealisticBeadsEnabled() {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.ToggleRealisticBeadsEnabled,
    });
    this.refreshIconNameRealisticBeadsVisibility();
  }

  showPaletteEditor(): void {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.PaletteEditor,
    });
  }

  showSubscription(): void {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.ShowSubscription,
    });
  }

  openPatternReader(): void {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.OpenPatternReader,
    });
  }

  canTransferImage(): boolean {
    return this.bcanvas.getPatternImage() !== undefined;
  }

  canRotateImage(): boolean {
    return (
      this.bcanvas.getPatternImage() !== undefined && this.isImageVisible()
    );
  }

  async transferImage(): Promise<void> {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.TransferImage,
    });
  }

  showTransformPattern(): void {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.TransformPattern,
    });
  }

  canUndo(): boolean {
    return this.bcanvas && this.bcanvas.canUndo();
  }

  undo(): void {
    if (this.bcanvas) {
      this.bcanvas.undo();
      
      // PostHog: Track undo action
      this.posthog.captureEvent('undo_action', {
        action_type: 'undo',
      });
    }
  }

  canRedo(): boolean {
    return this.bcanvas && this.bcanvas.canRedo();
  }

  redo(): void {
    if (this.bcanvas) {
      this.bcanvas.redo();

      // PostHog: Track redo action
      this.posthog.captureEvent('redo_action', {
        action_type: 'redo',
      });
    }
  }

  onInsertRows(): void {
    const dialogRef = this.dialog.open(InsertRowsColsModal, {
      width: '400px',
      hasBackdrop: false,
      disableClose: true,
      data: {
        operation: 'insert',
        mode: 'rows',
        currentCount: this.bcanvas.getRows(),
        bcanvas: this.bcanvas
      }
    });

    dialogRef.afterClosed().subscribe((result: InsertRowsColsModalResult) => {
      this.bcanvas.clearHighlight();

      if (result) {
        this.bcanvas.controller.insertRows(result.position, result.count);
        this.bcanvas.repaint();
        this.cdr.detectChanges();

        this.posthog.captureEvent('rows_inserted', {
          position: result.position,
          count: result.count,
          total_rows: this.bcanvas.getRows()
        });
      }
    });
  }

  onInsertCols(): void {
    const dialogRef = this.dialog.open(InsertRowsColsModal, {
      width: '400px',
      hasBackdrop: false,
      disableClose: true,
      data: {
        operation: 'insert',
        mode: 'cols',
        currentCount: this.bcanvas.getCols(),
        bcanvas: this.bcanvas
      }
    });

    dialogRef.afterClosed().subscribe((result: InsertRowsColsModalResult) => {
      this.bcanvas.clearHighlight();

      if (result) {
        this.bcanvas.controller.insertCols(result.position, result.count);
        this.bcanvas.repaint();
        this.cdr.detectChanges();

        this.posthog.captureEvent('cols_inserted', {
          position: result.position,
          count: result.count,
          total_cols: this.bcanvas.getCols()
        });
      }
    });
  }

  onRemoveRows(): void {
    const dialogRef = this.dialog.open(InsertRowsColsModal, {
      width: '400px',
      hasBackdrop: false,
      disableClose: true,
      data: {
        operation: 'delete',
        mode: 'rows',
        currentCount: this.bcanvas.getRows(),
        bcanvas: this.bcanvas
      }
    });

    dialogRef.afterClosed().subscribe((result: InsertRowsColsModalResult) => {
      this.bcanvas.clearHighlight();

      if (result) {
        this.bcanvas.controller.deleteRows(result.position, result.count);
        this.bcanvas.repaint();
        this.cdr.detectChanges();

        this.posthog.captureEvent('rows_deleted', {
          position: result.position,
          count: result.count,
          total_rows: this.bcanvas.getRows()
        });
      }
    });
  }

  onRemoveCols(): void {
    const dialogRef = this.dialog.open(InsertRowsColsModal, {
      width: '400px',
      hasBackdrop: false,
      disableClose: true,
      data: {
        operation: 'delete',
        mode: 'cols',
        currentCount: this.bcanvas.getCols(),
        bcanvas: this.bcanvas
      }
    });

    dialogRef.afterClosed().subscribe((result: InsertRowsColsModalResult) => {
      this.bcanvas.clearHighlight();

      if (result) {
        this.bcanvas.controller.deleteCols(result.position, result.count);
        this.bcanvas.repaint();
        this.cdr.detectChanges();

        this.posthog.captureEvent('cols_deleted', {
          position: result.position,
          count: result.count,
          total_cols: this.bcanvas.getCols()
        });
      }
    });
  }

  canZoomIn(): boolean {
    return this.bcanvas && this.bcanvas.canZoomIn();
  }

  zoomIn(): void {
    if (this.bcanvas) {
      this.bcanvas.zoomIn();
      
      // PostHog: Track zoom in
      this.posthog.captureEvent('zoom_action', {
        zoom_direction: 'in',
      });
    }
  }

  canZoomOut(): boolean {
    return this.bcanvas && this.bcanvas.canZoomOut();
  }

  zoomOut(): void {
    if (this.bcanvas) {
      this.bcanvas.zoomOut();
      
      // PostHog: Track zoom out
      this.posthog.captureEvent('zoom_action', {
        zoom_direction: 'out',
      });
    }
  }

  canShowRealisticBeads(): boolean {
    return this.bcanvas && this.bcanvas.canShowRealisticBeads();
  }

  toggleRightSidebar(): void {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.ToggleRightSidebar,
    });
  }

  getPhotoUrl() {
    if (
      this.httpService.socialUser &&
      this.httpService.socialUser.photoUrl &&
      this.httpService.socialUser.photoUrl != ''
    ) {
      return this.httpService.socialUser.photoUrl;
    } else {
      return 'https://www.w3schools.com/howto/img_avatar.png';
    }
  }

  getFullname() {
    if (this.httpService.socialUser)
      return (
        this.httpService.socialUser.firstName +
        ' ' +
        this.httpService.socialUser.lastName +
        ' (' +
        this.getProviderName(this.httpService.socialUser.provider) +
        ')'
      );
    else return 'Anonymous';
  }

  getProviderName(provider: string) {
    if (provider === 'FACEBOOK') {
      return this.httpService.getTransation().Facebook;
    } else if (provider === 'GOOGLE') {
      return this.httpService.getTransation().Google;
    }
    return provider; // Return the provider string itself if unknown
  }

  public async showProfile() {
    const dialogRef = this.dialog.open(ProfileModal, {
      width: '90%',
      maxWidth: '380px',
      data: {
        unitOfMeasure: this.httpService.user.unitOfMeasure,
        language: this.httpService.user.language,
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.httpService.user.unitOfMeasure = result.unitOfMeasure;
        this.httpService.user.language = result.language;

        try {
          await this.httpService.updateUserProfile();

          // Emit translation changed event so UI updates
          this.httpService.onTranslationChanged.emit(this.httpService.translation);
        } catch (error) {
          console.error('[ToolbarComponent] Failed to update profile:', error);
          // Translation will still be updated locally, so the UI will reflect changes
          // even if the server update failed
        }
      }
    });
  }

  async manageSubscription(): Promise<void> {
    if (this.httpService.user) {
      // Check if user has an active (non-expired) subscription
      if (
        this.httpService.user.stripeCustomerId &&
        this.httpService.isSubscriptionActive()
      ) {
        // User has an active subscription - open Stripe Customer Portal
        this.posthog.captureEvent('customer_portal_opened', {
          subscription_type: this.httpService.user.subscriptionType,
          source: 'profile_menu',
        });

        await this.showCustomerPortal();
      } else {
        // User is free or has expired subscription - show pricing modal
        this.showSubscription();
      }
    }
  }

  async showCustomerPortal(): Promise<void> {
    try {
      const data = await this.httpService.createCustomerPortalSession();
      if (data && data.redirectUrl) {
        window.open(data.redirectUrl, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      // Fallback to showing subscription modal
      this.showSubscription();
    }
  }

  async signOut() {
    console.log('[ToolbarComponent] signOut called');
    console.log('[ToolbarComponent] Current login status:', this.httpService.loggedIn);
    console.log('[ToolbarComponent] Current userId:', this.httpService.userId);
    console.log('[ToolbarComponent] User Agent:', navigator.userAgent);
    
    // Detect if running on Chrome Android early
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isChrome = /Chrome/i.test(navigator.userAgent) && !/Edge|Edg/i.test(navigator.userAgent);
    
    if (isAndroid && isChrome) {
      // On Chrome Android, clear ALL localStorage to prevent stale state issues
      console.log('[ToolbarComponent] Chrome Android detected - clearing ALL localStorage');
      localStorage.clear();
    } else {
      // On other platforms, just remove the auth provider
      localStorage.removeItem(LocalStorageKeys.AuthProvider);
      console.log('[ToolbarComponent] Removed AuthProvider from localStorage');
    }
    
    if (this.httpService.loggedIn) {
      console.log('[ToolbarComponent] User is logged in, calling signOut on Google auth service');
      
      // Sign out from Google Identity Services
      try {
        this.googleAuthService.signOut();
        console.log('[ToolbarComponent] GoogleAuthService.signOut() completed');
      } catch (error) {
        console.log('[ToolbarComponent] GoogleAuthService.signOut() error:', error);
      }
    } else {
      console.log('[ToolbarComponent] User is NOT logged in, skipping authService.signOut()');
    }

    // Reset httpService state
    this.httpService.loggedIn = false;
    this.httpService.socialUser = null;
    this.httpService.userId = null;
    console.log('[ToolbarComponent] Reset httpService state');

    // Unregister service workers if present (Chrome Android issue fix)
    if ('serviceWorker' in navigator) {
      console.log('[ToolbarComponent] Unregistering service workers');
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
          console.log('[ToolbarComponent] Service worker unregistered');
        }
      } catch (error) {
        console.error('[ToolbarComponent] Error unregistering service workers:', error);
      }
    }

    console.log('[ToolbarComponent] Platform detection:', { isAndroid, isChrome });

    if (isAndroid && isChrome) {
      console.log('[ToolbarComponent] Chrome Android detected - forcing full page reload');
      // Force full page reload to clear any stale state
      window.location.href = '/login?nocache=' + new Date().getTime();
    } else {
      console.log('[ToolbarComponent] Navigating to login page');
      this.router.navigateByUrl('login').then(() => {
        console.log('[ToolbarComponent] Navigation to login completed');
      }).catch((error) => {
        console.error('[ToolbarComponent] Navigation to login failed:', error);
      });
    }
  }

  // Welcome tour removed - replaced with new onboarding system
  // welcomeTour() {
  //   this.toolbarActionFired.emit(<ToolbarActionFired>{
  //     actionType: ToolbarActionFiredTypeEnum.StartWelcomeTour,
  //   });
  // }

  videoTutorial() {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.VideoTutorial,
    });
  }

  toggleHelpPanel() {
    this.toolbarActionFired.emit(<ToolbarActionFired>{
      actionType: ToolbarActionFiredTypeEnum.ToggleHelpPanel,
    });
  }

  private highlightedMenuItem: string | null = null;

  /**
   * Open a specific menu for the checklist (public method)
   * Called when user clicks a checklist step on mobile
   * Opens both the dots menu and the appropriate submenu (File or Image)
   * @param menu - Which menu to open ('file' or 'image')
   * @param highlightItem - Which specific menu item to highlight (optional)
   */
  public openMenuForChecklist(menu: 'file' | 'image', highlightItem?: string): void {
    this.highlightedMenuItem = highlightItem || null;
    this.openMobileMenuHierarchy(menu);
  }

  /**
   * Check if a menu item should be highlighted
   */
  public isMenuItemHighlighted(itemId: string): boolean {
    return this.highlightedMenuItem === itemId;
  }

  /**
   * Clear the highlighted menu item
   */
  public clearHighlight(): void {
    this.highlightedMenuItem = null;
  }

  /**
   * Open a specific menu for onboarding hints
   * Only works on mobile when menus are hidden (windowWidth <= minMainMenuWidth)
   * Opens both the dots menu and the appropriate submenu (File or Image)
   */
  private openMenuForOnboarding(menu: 'file' | 'image' | null): void {
    if (menu) {
      this.openMobileMenuHierarchy(menu);
    }
  }

  /**
   * Core menu opening logic for mobile devices
   * Opens both the dots menu and the appropriate submenu (File or Image)
   */
  private openMobileMenuHierarchy(menu: 'file' | 'image'): void {
    // Only open menus on mobile devices where the menu bar is hidden
    if (this.windowWidth > this.minMainMenuWidth) {
      return;
    }

    // Find the dots menu trigger
    const dotsMenuTrigger = this.menuTriggers?.find(trigger => {
      const element = (trigger as any)._element?.nativeElement;
      return element?.classList?.contains('dots-menu-button');
    });

    if (dotsMenuTrigger && !dotsMenuTrigger.menuOpen) {
      // Step 1: Open the dots menu
      dotsMenuTrigger.openMenu();
      console.log('[Toolbar] Opened dots menu');

      // Step 2: After dots menu is open, open the appropriate submenu
      setTimeout(() => {
        let submenuTrigger: MatMenuTrigger | null = null;
        
        if (menu === 'file') {
          submenuTrigger = this.mobileFileMenuTrigger;
        } else if (menu === 'image') {
          submenuTrigger = this.mobileImageMenuTrigger;
        }

        if (submenuTrigger && !submenuTrigger.menuOpen) {
          submenuTrigger.openMenu();
          console.log(`[Toolbar] Opened ${menu} submenu`);
        }
      }, 400); // Wait for dots menu animation to complete
    }
  }
}

export class ToolbarActionFired {
  actionType: ToolbarActionFiredTypeEnum;
  parameters: any;
}

export enum ToolbarActionFiredTypeEnum {
  New,
  Open,
  Save,
  SaveAs,
  Print,
  PaletteEditor,
  UploadImage,
  TransferImage,
  TransformPattern,
  ToggleImageVisibility,
  ToggleRealisticBeadsEnabled,
  ShiftLeft,
  ShiftRight,
  ShiftUp,
  ShiftDown,
  RotateLeft,
  RotateRight,
  FlipVertically,
  FlipHorizontally,
  RotateImageLeft,
  RotateImageRight,
  RotatedPatternOn,
  RotatedPatternOff,
  ShowSubscription,
  ToggleRightSidebar,
  StartWelcomeTour,
  VideoTutorial,
  ToggleHelpPanel,
  OpenPatternReader,
}
